import React, { useState, useEffect, useRef, cloneElement } from "react";
import { HeaderTitleComponent } from "@/components/DataTable/Header/Title";
import {
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Row,
  type Table as TableType,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  PaginationState,
  type ColumnPinningState,
} from "@tanstack/react-table";
import { DataTableContext } from "@/components/DataTable/Context/DataTableContext";
import { FilterTableComponent } from "./Header/Filter";
import { BodyCellsComponent } from "./Body/BodyCells";
import { HeaderCellsComponent } from "./Header/Cells";
import { THeaderCell } from "./Header/THeaderCell";
import { THeaderCellSorting } from "./Header/THeaderCellSorting";
import { TBodyCell } from "./Body/TBodyCell";
import { ControlsPaginationComponent } from "./Pagination/Controls";
import { PaginationLabelComponent } from "./Pagination/Label";
import { HeaderActionsComponent } from "./Header/HeaderActions";
import { EmptyState } from "./States/Empty";
import { Table } from "@/components/ui";
import { useTranslation } from "react-i18next";
import { RowSelectionState, OnChangeFn } from "@tanstack/react-table";

export interface DataTableProps<T> {
  columns?: ColumnDef<T>[];
  data: T[];
  topHeader?: React.ReactNode;
  tableHeader?: React.ReactNode;
  tableBody?: React.ReactNode;
  pagination?: React.ReactNode;
  paginationLabel?: React.ReactNode;
  emptyState?: React.ReactNode;
  isLoading?: boolean;
  numSkeletonRows?: number;
  ariaLabelTableSection?: string;
  ariaLabelTableContent?: string;
  ariaLabelTablePagination?: string;
  paginationState?: PaginationState;
  setPaginationState?: React.Dispatch<React.SetStateAction<PaginationState>>;
  initialSortState?: SortingState;
  enableColumnPinning?: boolean;
  initialState?: {
    columnPinning?: ColumnPinningState;
    [key: string]: unknown;
  };
  tableContentClassName?: string;
  rowSelection?: Record<number, boolean>;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  className?: string;
  getRowId?: (row: T) => string;
  enableRowSelection?: (row: Row<T>) => boolean;
  [key: string]: unknown;
}

export function DataTable<T>({
  columns,
  data,
  topHeader,
  tableHeader,
  tableBody,
  pagination,
  emptyState = <EmptyState />,
  isLoading = false,
  numSkeletonRows = 5,
  ariaLabelTableSection,
  ariaLabelTableContent,
  ariaLabelTablePagination,
  paginationState,
  setPaginationState,
  initialSortState = [],
  enableColumnPinning = false,
  initialState,
  tableContentClassName,
  onRowSelectionChange,
  getRowId,
  className = "",
  enableRowSelection = () => true,
  ...props
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>(initialSortState);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    initialState?.columnPinning || { left: [], right: [] },
  );
  const [defaultPaginationState, setDefaultPaginationState] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: 5,
    });

  const [renderedDimensions, setRenderedDimensions] = useState<{
    contentDimensions: Record<string, { width: number; height: number }>;
  }>({
    contentDimensions: {},
  });

  const tableBodyRef = useRef<HTMLTableSectionElement>(null);

  const table = useReactTable({
    data,
    columns: columns as ColumnDef<T>[],
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: onRowSelectionChange || setRowSelection,
    onPaginationChange: setPaginationState || setDefaultPaginationState,
    onColumnPinningChange: setColumnPinning,
    enableSorting: true,
    enableRowSelection: enableRowSelection,
    getRowId: getRowId,
    enableColumnPinning,
    initialState: {
      sorting: initialSortState,
      columnPinning: initialState?.columnPinning || { left: [], right: [] },
      ...initialState,
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection: props.rowSelection || rowSelection,
      pagination: paginationState || defaultPaginationState,
      columnPinning,
    },
    ...props,
  });

  useEffect(() => {
    if (!isLoading && data.length > 0 && columns && tableBodyRef.current) {
      const timeoutId = setTimeout(() => {
        const contentDimensions: Record<
          string,
          { width: number; height: number }
        > = {};

        const firstRow = tableBodyRef.current?.querySelector('tr[role="row"]');
        if (firstRow) {
          const cells = firstRow.querySelectorAll('td[role="cell"]');

          cells.forEach((cell, index) => {
            const column = columns[index];
            if (column?.id) {
              const contentElement = cell.querySelector(".p-4") || cell;
              const rect = contentElement.getBoundingClientRect();

              const computedStyle = window.getComputedStyle(contentElement);
              const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
              const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
              const totalHorizontalPadding = paddingLeft + paddingRight;
              contentDimensions[column.id] = {
                width: Math.max(rect.width - totalHorizontalPadding, 16),
                height: Math.max(rect.height, 24),
              };
            }
          });
        }

        setRenderedDimensions({ contentDimensions });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, data, columns]);

  return (
    <DataTableContext.Provider
      value={{
        columns: columns as ColumnDef<unknown>[],
        data: data as unknown[],
        table: table as unknown as TableType<unknown>,
        isLoading,
        numSkeletonRows,
        renderedDimensions,
      }}
    >
      <section
        role="region"
        className="flex justify-between gap-4 pr-6"
        aria-label={String(
          ariaLabelTableSection || t("dataTable.dataTableSection"),
        )}
      >
        {topHeader}
      </section>
      <section
        role="region"
        className={tableContentClassName}
        aria-label={String(
          ariaLabelTableContent || t("dataTable.dataTableContent"),
        )}
      >
        {data.length || isLoading ? (
          <div
            style={{
              overflow: "auto",
              width: "100%",
              height: "100%",
            }}
          >
            <Table
              style={{ borderCollapse: "separate", borderSpacing: 0 }}
              className={className}
            >
              {tableHeader}
              {cloneElement(
                (tableBody || <BodyCellsComponent />) as React.ReactElement<{
                  ref?: React.Ref<HTMLTableSectionElement>;
                }>,
                {
                  ref: tableBodyRef,
                },
              )}
            </Table>
          </div>
        ) : (
          emptyState
        )}
      </section>
      <section
        role="region"
        className="flex justify-between"
        aria-label={String(
          ariaLabelTablePagination || t("dataTable.tablePagination"),
        )}
      >
        {pagination}
      </section>
    </DataTableContext.Provider>
  );
}

DataTable.Title = HeaderTitleComponent;
DataTable.Filter = FilterTableComponent;
DataTable.TBody = BodyCellsComponent;
DataTable.THeader = HeaderCellsComponent;
DataTable.THeaderCell = THeaderCell;
DataTable.THeaderCellSorting = THeaderCellSorting;
DataTable.TBodyCell = TBodyCell;
DataTable.PaginationControls = ControlsPaginationComponent;
DataTable.PaginationLabel = PaginationLabelComponent;
DataTable.HeaderActions = HeaderActionsComponent;
